import React, { useState, useEffect } from 'react';
import {
  X, Edit2, Users, Phone, Mail, MapPin, Building2, Star,
  FileText, CreditCard, Landmark, Calendar, ClipboardList,
  IndianRupee, TrendingUp, TrendingDown, Minus, Package,
  Loader2, Clock, Truck, Shield, Activity, FolderOpen,
  AlertTriangle, CheckCircle, XCircle, ArrowUpRight,
  ArrowDownRight, UserCheck, Gauge, BarChart3,
} from 'lucide-react';
import supplierService from '../../../../services/supplierService';
import { useAuth } from '../../../../context/AuthContext';
import { P } from '@/config/permissionCodes';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Users },
  { id: 'orders', label: 'Orders', icon: Package },
  { id: 'pricelist', label: 'Price List', icon: IndianRupee },
  { id: 'performance', label: 'Performance', icon: Gauge },
  { id: 'documents', label: 'Documents', icon: FolderOpen },
  { id: 'activity', label: 'Activity', icon: Activity },
];

export default function SupplierDetail({ supplier, onClose, onEdit }) {
    const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [orderHistory, setOrderHistory] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const canEdit = hasPermission(P.INVENTORY.SUPPLIER.EDIT);

  if (!supplier) return null;

  // Fetch tab data on tab switch
  useEffect(() => {
    const load = async () => {
      setTabLoading(true);
      try {
        if (activeTab === 'orders' && orderHistory.length === 0) {
          const data = await supplierService.getOrderHistory(supplier.id);
          setOrderHistory(Array.isArray(data) ? data : []);
        }
        if (activeTab === 'pricelist' && priceList.length === 0) {
          const data = await supplierService.getPriceList(supplier.id);
          setPriceList(Array.isArray(data) ? data : []);
        }
        if (activeTab === 'performance' && !performance) {
          const data = await supplierService.getPerformance(supplier.id);
          setPerformance(data);
        }
        if (activeTab === 'documents' && documents.length === 0) {
          const data = await supplierService.getDocuments(supplier.id);
          setDocuments(Array.isArray(data) ? data : []);
        }
        if (activeTab === 'activity' && activityLog.length === 0) {
          const data = await supplierService.getActivityLog(supplier.id);
          setActivityLog(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load tab data:', err);
      } finally {
        setTabLoading(false);
      }
    };
    load();
  }, [activeTab, supplier.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-teal-500 flex items-center justify-center">
                <Users size={22} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-slate-800">{supplier.name}</h2>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    supplier.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                  }`}>
                    {supplier.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
                  {supplier.category && <span className="bg-slate-200/60 px-1.5 py-0.5 rounded text-[10px] font-medium">{supplier.category}</span>}
                  {supplier.contactPerson && <span>{supplier.contactPerson}</span>}
                  {supplier.city && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{supplier.city}</span>}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 -mb-4 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border border-b-0 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-teal-700 border-slate-200'
                      : 'bg-transparent text-slate-500 border-transparent hover:text-slate-700 hover:bg-white/50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tabLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 text-teal-500 animate-spin" />
              <span className="ml-2 text-sm text-slate-500">Loading...</span>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab supplier={supplier} />}
              {activeTab === 'orders' && <OrdersTab orders={orderHistory} />}
              {activeTab === 'pricelist' && <PriceListTab items={priceList} />}
              {activeTab === 'performance' && <PerformanceTab data={performance} supplier={supplier} />}
              {activeTab === 'documents' && <DocumentsTab documents={documents} />}
              {activeTab === 'activity' && <ActivityTab logs={activityLog} />}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-[10px] text-slate-400 space-x-3">
            <span>Added: {new Date(supplier.createdAt).toLocaleDateString('en-IN')}</span>
            <span>Updated: {new Date(supplier.updatedAt).toLocaleDateString('en-IN')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
              Close
            </button>
            {canEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" />
              Edit Supplier
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Overview Tab ──────────────────────────────────────────── */
function OverviewTab({ supplier }) {
  const score = supplier.performance
    ? (supplier.performance.quality * 0.4 + supplier.performance.delivery * 0.3 + supplier.performance.cost * 0.2 + supplier.performance.service * 0.1).toFixed(1)
    : null;

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard icon={ClipboardList} label="Total Orders" value={supplier.totalOrders || 0} color="teal" />
        <SummaryCard icon={IndianRupee} label="Total Value" value={`₹${(supplier.totalValue || 0).toLocaleString('en-IN')}`} color="blue" />
        <SummaryCard
          icon={Star}
          label="Rating"
          value={supplier.rating > 0 ? supplier.rating : '—'}
          color="amber"
          extra={supplier.rating > 0 && (
            <div className="flex gap-0.5 mt-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-3 h-3 ${i <= Math.round(supplier.rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
              ))}
            </div>
          )}
        />
        <SummaryCard icon={Gauge} label="Performance" value={score || '—'} color="green" />
      </div>

      {/* Contact & Delivery Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Contact Information */}
        <Section title="Contact Information">
          <div className="grid grid-cols-2 gap-3">
            <InfoField icon={Phone} label="Phone" value={supplier.phone} />
            <InfoField icon={Phone} label="Alt. Phone" value={supplier.altPhone} />
            <InfoField icon={Mail} label="Email" value={supplier.email} />
            <InfoField icon={Building2} label="Category" value={supplier.category} />
          </div>
          {supplier.contacts && supplier.contacts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-2">Contacts</p>
              <div className="space-y-1.5">
                {supplier.contacts.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
                    <UserCheck className="w-3 h-3 text-teal-500 flex-shrink-0" />
                    <span className="font-medium text-slate-700">{c.name}</span>
                    {c.role && <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{c.role}</span>}
                    {c.phone && <span className="text-slate-500 ml-auto">{c.phone}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Delivery & Ordering */}
        <Section title="Delivery & Ordering">
          <div className="grid grid-cols-2 gap-3">
            <InfoField icon={Calendar} label="Payment Terms" value={supplier.paymentTerms} />
            <InfoField icon={Truck} label="Order Method" value={supplier.orderMethod} />
            <InfoField icon={Clock} label="Lead Time" value={supplier.leadTimeDays != null ? `${supplier.leadTimeDays} day${supplier.leadTimeDays !== 1 ? 's' : ''}` : null} />
            <InfoField icon={IndianRupee} label="Min Order" value={supplier.minOrderAmount ? `₹${supplier.minOrderAmount.toLocaleString('en-IN')}` : null} />
          </div>
          {supplier.deliveryDays && supplier.deliveryDays.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">Delivery Days</p>
              <div className="flex gap-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <span key={d} className={`w-8 h-7 flex items-center justify-center rounded text-[10px] font-medium ${
                    supplier.deliveryDays.includes(d) ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Address & Tax/Bank */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Section title="Address">
          <div className="grid grid-cols-2 gap-3">
            <InfoField icon={MapPin} label="Address" value={supplier.address} colSpan />
            <InfoField icon={MapPin} label="City" value={supplier.city} />
            <InfoField icon={MapPin} label="State" value={supplier.state} />
            <InfoField icon={MapPin} label="Pincode" value={supplier.pincode} />
          </div>
        </Section>

        <Section title="Tax & Bank Details">
          <div className="grid grid-cols-2 gap-3">
            <InfoField icon={FileText} label="GSTIN" value={supplier.gstin} />
            <InfoField icon={FileText} label="PAN" value={supplier.pan} />
            <InfoField icon={Shield} label="FSSAI" value={supplier.fssai} />
            <InfoField icon={Landmark} label="Bank" value={supplier.bankName} />
            <InfoField icon={CreditCard} label="Account" value={supplier.bankAccount} />
            <InfoField icon={Landmark} label="IFSC" value={supplier.ifscCode} />
          </div>
        </Section>
      </div>

      {/* Notes */}
      {supplier.notes && (
        <Section title="Notes">
          <p className="text-xs text-slate-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
            {supplier.notes}
          </p>
        </Section>
      )}
    </div>
  );
}

/* ─── Orders Tab ────────────────────────────────────────────── */
function OrdersTab({ orders }) {
  const statusBadge = {
    draft: 'bg-slate-100 text-slate-600',
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    received: 'bg-green-100 text-green-700',
    partially_received: 'bg-violet-100 text-violet-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  if (orders.length === 0) {
    return <EmptyTab icon={Package} message="No order history available for this supplier" />;
  }

  const totalValue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const receivedCount = orders.filter((o) => o.status === 'received').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="font-medium text-slate-700">{orders.length} orders</span>
        <span>{receivedCount} received</span>
        <span>Total: ₹{totalValue.toLocaleString('en-IN')}</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">PO #</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Date</th>
              <th className="text-center px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Items</th>
              <th className="text-right px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
              <th className="text-center px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-medium text-teal-600">{o.id}</td>
                <td className="px-4 py-2.5 text-slate-600">{new Date(o.date).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-2.5 text-center text-slate-600">{o.items}</td>
                <td className="px-4 py-2.5 text-right font-medium text-slate-800">₹{(o.total || 0).toLocaleString('en-IN')}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadge[o.status] || 'bg-slate-100 text-slate-600'}`}>
                    {(o.status || '').replace('_', ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Price List Tab ────────────────────────────────────────── */
function PriceListTab({ items }) {
  if (items.length === 0) {
    return <EmptyTab icon={IndianRupee} message="No price list available for this supplier" />;
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">{items.length} items in price list</p>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Item</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">SKU</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Pack Size</th>
              <th className="text-right px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Rate (₹)</th>
              <th className="text-right px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Previous</th>
              <th className="text-center px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Change</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-600 uppercase tracking-wide">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5">
                  <span className="font-medium text-slate-800">{item.itemName}</span>
                  <span className="text-slate-400 ml-1">/ {item.uom}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-[10px]">{item.sku}</td>
                <td className="px-4 py-2.5 text-slate-600">{item.packSize}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-slate-800">₹{item.unitPrice}</td>
                <td className="px-4 py-2.5 text-right text-slate-400">₹{item.lastPrice}</td>
                <td className="px-4 py-2.5 text-center">
                  <PriceChangeIndicator change={item.priceChange} />
                </td>
                <td className="px-4 py-2.5 text-slate-500">{new Date(item.lastUpdated).toLocaleDateString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PriceChangeIndicator({ change }) {
  if (!change || change === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-slate-400 text-[11px]">
        <Minus className="w-3 h-3" /> 0%
      </span>
    );
  }
  if (change > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-red-600 text-[11px] font-medium">
        <ArrowUpRight className="w-3 h-3" /> +{change}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-green-600 text-[11px] font-medium">
      <ArrowDownRight className="w-3 h-3" /> {change}%
    </span>
  );
}

/* ─── Performance Tab ───────────────────────────────────────── */
function PerformanceTab({ data, supplier }) {
  if (!data) {
    return <EmptyTab icon={Gauge} message="No performance data available for this supplier" />;
  }

  const scoreColor = (v) => {
    if (v >= 4.5) return 'text-green-600';
    if (v >= 4.0) return 'text-teal-600';
    if (v >= 3.5) return 'text-amber-600';
    return 'text-red-600';
  };

  const barColor = (v) => {
    if (v >= 4.5) return 'bg-green-500';
    if (v >= 4.0) return 'bg-teal-500';
    if (v >= 3.5) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const metrics = [
    { label: 'Quality', value: data.quality, weight: '40%' },
    { label: 'Delivery', value: data.delivery, weight: '30%' },
    { label: 'Cost', value: data.cost, weight: '20%' },
    { label: 'Service', value: data.service, weight: '10%' },
  ];

  return (
    <div className="space-y-5">
      {/* Overall Score */}
      <div className="bg-slate-50 rounded-xl p-5 text-center">
        <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Weighted Performance Score</p>
        <p className={`text-4xl font-bold ${scoreColor(data.weightedScore)}`}>{data.weightedScore}</p>
        <p className="text-xs text-slate-400 mt-1">out of 5.0</p>
        <div className="flex items-center justify-center gap-1 mt-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className={`w-5 h-5 ${i <= Math.round(data.weightedScore) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
          ))}
        </div>
      </div>

      {/* Metrics Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-700">{m.label}</span>
              <span className="text-[10px] text-slate-400">Weight: {m.weight}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor(m.value)}`}
                  style={{ width: `${(m.value / 5) * 100}%` }}
                />
              </div>
              <span className={`text-sm font-bold ${scoreColor(m.value)}`}>{m.value}</span>
            </div>
          </div>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-teal-600 mb-1">On-Time Rate</p>
          <p className="text-2xl font-bold text-teal-700">{data.onTimeRate}%</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-600 mb-1">Order Accuracy</p>
          <p className="text-2xl font-bold text-blue-700">{data.orderAccuracy}%</p>
        </div>
        <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-violet-600 mb-1">Avg Lead Time</p>
          <p className="text-2xl font-bold text-violet-700">{data.avgLeadTime}d</p>
        </div>
      </div>

      {/* Summary */}
      <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 flex items-center gap-4">
        <span>Total Orders: <strong className="text-slate-700">{data.totalOrders}</strong></span>
        <span>Total Value: <strong className="text-slate-700">₹{(data.totalValue || 0).toLocaleString('en-IN')}</strong></span>
      </div>
    </div>
  );
}

/* ─── Documents Tab ─────────────────────────────────────────── */
function DocumentsTab({ documents }) {
  if (documents.length === 0) {
    return <EmptyTab icon={FolderOpen} message="No documents uploaded for this supplier" />;
  }

  const statusConfig = {
    valid: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100 text-green-700', label: 'Valid' },
    expiring_soon: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100 text-amber-700', label: 'Expiring Soon' },
    expired: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100 text-red-700', label: 'Expired' },
  };

  const typeColors = {
    License: 'bg-blue-100 text-blue-700',
    Tax: 'bg-teal-100 text-teal-700',
    Contract: 'bg-violet-100 text-violet-700',
    Quality: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>{documents.length} documents</span>
        {documents.some((d) => d.status === 'expiring_soon') && (
          <span className="flex items-center gap-1 text-amber-600 font-medium">
            <AlertTriangle className="w-3 h-3" />
            {documents.filter((d) => d.status === 'expiring_soon').length} expiring soon
          </span>
        )}
        {documents.some((d) => d.status === 'expired') && (
          <span className="flex items-center gap-1 text-red-600 font-medium">
            <XCircle className="w-3 h-3" />
            {documents.filter((d) => d.status === 'expired').length} expired
          </span>
        )}
      </div>
      <div className="space-y-2">
        {documents.map((doc) => {
          const st = statusConfig[doc.status] || statusConfig.valid;
          const StIcon = st.icon;
          return (
            <div key={doc.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:bg-slate-50/50 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800">{doc.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${typeColors[doc.type] || 'bg-slate-100 text-slate-600'}`}>
                    {doc.type}
                  </span>
                  <span className="text-[10px] text-slate-400">Uploaded: {new Date(doc.uploadDate).toLocaleDateString('en-IN')}</span>
                </div>
              </div>
              {doc.expiryDate && (
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-slate-400">Expires</p>
                  <p className={`text-xs font-medium ${doc.status === 'expired' ? 'text-red-600' : doc.status === 'expiring_soon' ? 'text-amber-600' : 'text-slate-600'}`}>
                    {new Date(doc.expiryDate).toLocaleDateString('en-IN')}
                  </p>
                </div>
              )}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.bg}`}>
                <StIcon className="w-3 h-3" />
                {st.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Activity Tab ──────────────────────────────────────────── */
function ActivityTab({ logs }) {
  if (logs.length === 0) {
    return <EmptyTab icon={Activity} message="No activity recorded for this supplier" />;
  }

  const typeConfig = {
    status: { color: 'bg-blue-500', icon: Users },
    price: { color: 'bg-amber-500', icon: IndianRupee },
    order: { color: 'bg-teal-500', icon: Package },
    delivery: { color: 'bg-green-500', icon: Truck },
    payment: { color: 'bg-violet-500', icon: CreditCard },
    note: { color: 'bg-slate-400', icon: FileText },
  };

  return (
    <div className="space-y-0">
      {logs.map((log, idx) => {
        const cfg = typeConfig[log.type] || typeConfig.note;
        const LogIcon = cfg.icon;
        return (
          <div key={log.id} className="flex gap-3">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full ${cfg.color} flex items-center justify-center flex-shrink-0`}>
                <LogIcon className="w-3.5 h-3.5 text-white" />
              </div>
              {idx < logs.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
            </div>
            {/* Content */}
            <div className="pb-4 flex-1 min-w-0">
              <p className="text-xs text-slate-700">{log.description}</p>
              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                <span>{new Date(log.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <span>{new Date(log.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                <span className="bg-slate-100 px-1.5 py-0.5 rounded font-medium">{log.actor}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Shared Components ─────────────────────────────────────── */
function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-2">{title}</h3>
      <div className="bg-slate-50 rounded-xl p-4">{children}</div>
    </div>
  );
}

function InfoField({ icon: Icon, label, value, colSpan }) {
  return (
    <div className={colSpan ? 'sm:col-span-2' : ''}>
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className="w-3 h-3 text-slate-400" />
        <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">{label}</span>
      </div>
      <p className="text-xs text-slate-700 font-medium">{value || '—'}</p>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color, extra }) {
  const bgMap = { teal: 'bg-teal-50', blue: 'bg-blue-50', amber: 'bg-amber-50', green: 'bg-green-50' };
  const iconBgMap = { teal: 'bg-teal-100', blue: 'bg-blue-100', amber: 'bg-amber-100', green: 'bg-green-100' };
  const iconColorMap = { teal: 'text-teal-600', blue: 'text-blue-600', amber: 'text-amber-600', green: 'text-green-600' };

  return (
    <div className={`${bgMap[color]} rounded-xl p-3`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg ${iconBgMap[color]} flex items-center justify-center`}>
          <Icon className={`w-3.5 h-3.5 ${iconColorMap[color]}`} />
        </div>
      </div>
      <p className="text-lg font-bold text-slate-800">{value}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
      {extra}
    </div>
  );
}

function EmptyTab({ icon: Icon, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-slate-300" />
      </div>
      <p className="text-sm text-slate-500">{message}</p>
      <p className="text-xs text-slate-400 mt-1">Data will appear here once available</p>
    </div>
  );
}
